import React, { useMemo, useCallback, useEffect, useState } from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { DetailCard } from '@tecuity/granite-ui/DetailCard'
import { Detail } from '@tecuity/granite-ui/Detail'
import { Spinner } from '../../Loaders/Loaders'
import { Report } from '../../MyWorkQueue/ReportsList'
import format from 'date-fns/format'
import './UccGeneralPanel.scss'
import { DetailList } from '@tecuity/granite-ui'
import { useHistory } from 'react-router'
import * as api from '../../../requests'
import { useRequestData } from '../../../Utilities/hooks'
// import { returnEmptyStringForNull } from '../../../Utilities/Utilities'
import { checkDLNValid } from '../../BOSearch/BOSearchActions'
import { openSpecificImage } from '../../ImageViewer/ImageViewerActions'
import { setErrorToast } from '../../../actions'
import RecordHistory from 'components/RecordHistory/RecordHistory'
import { notNullOrUndefined } from 'utilities'

const UccGeneralPanel = ({ currentUccNum }) => {
  const { ucc } = useSelector((state) => state.record, shallowEqual)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (ucc && ucc.FilingNumber === currentUccNum) setLoading(false)
    else setLoading(true)
  }, [ucc, currentUccNum])

  if (loading)
    return (
      <div className="loader-wrapper">
        <Spinner className="page-loader" themeColor="dark-neutral" />
      </div>
    )

  if (ucc !== undefined && ucc.FilingNumber) {
    return (
      <div className="ucc-general-panel">
        <h2>{ucc.UCCFilingType}</h2>
        <div className="ucc-general-row">
          <div className="ucc-general-col">
            <Submitter ucc={ucc} />
            <AuthorizingParty ucc={ucc} />
          </div>
          <DocumentInformation ucc={ucc} />
        </div>
        <InformationStatementDetail ucc={ucc} />
        <DebtorsAndSecuredParties ucc={ucc} />
        <LienCounties ucc={ucc} />
        <Collateral ucc={ucc} />
        <Miscellaneous ucc={ucc} />
        <EFSInfo />
        <VoidInfo ucc={ucc} />
        <OfficerCorrectionInfo ucc={ucc} />
        <DishonoredPaymentInfo ucc={ucc} />
        <DownloadDocuments />
        <Corrections />
      </div>
    )
  } else return null
}

export default UccGeneralPanel

const addField = (config = {}, fieldName, value, array) => {
  if (config[fieldName] && config[fieldName].Enabled == true) {
    array.push({ label: config[fieldName].DisplayName, value: value })
  }
}

const Submitter = ({ ucc }) => {
  const { notaryRecordDetailsSettings: config } = useSelector((state) => state.record, shallowEqual)

  const showContactInfo = useMemo(() => {
    return !!(ucc.ContactName || ucc.ContactPhone || ucc.ContactEmail)
  }, [ucc])

  const submitterDetails = useMemo(() => {
    const details = []
    if (showContactInfo) {
      if (ucc.ContactName) addField(config, 'ContactName', ucc.ContactName, details)
      if (ucc.ContactPhone) addField(config, 'ContactPhone', ucc.ContactPhone, details)
      if (ucc.ContactEmail) addField(config, 'ContactEmail', ucc.ContactEmail, details)
    }
    addField(config, 'SubmitterNameAndAddress', ucc.SubmitterNameAndAddress, details) // this may need to be parsed.
    return details
  }, [ucc, showContactInfo, config])

  return (
    <div>
      <DetailCard label="Submitter" className="detailsCard">
        <DetailList details={submitterDetails} topBorder bottomBorder />
      </DetailCard>
    </div>
  )
}

const AuthorizingParty = ({ ucc }) => {
  const { notaryRecordDetailsSettings: config } = useSelector((state) => state.record, shallowEqual)

  const party = useMemo(() => {
    // console.log(ucc.AuthorizingParty)
    return ucc.AuthorizingParty || {}
  }, [ucc])

  const notAvailable = useMemo(() => {
    return (
      !party.OrganizationName &&
      !party.FirstName &&
      !party.MiddleName &&
      !party.LastName &&
      !party.Suffix
    )
  }, [party])

  const partyName = useMemo(() => {
    if (notAvailable) return null
    return `${party.FirstName}${party.MiddleName ? ` ${party.MiddleName}` : ``}${
      party.LastName ? ` ${party.LastName}` : ``
    }${party.Suffix ? ` ${party.Suffix}` : ``}`.trim()
  }, [party, notAvailable])

  const authorizingPartyDetails = useMemo(() => {
    const details = []
    if (party.OrganizationName)
      addField(config, 'AuthorizationPartyOrgName', party.OrganizationName, details)
    if (partyName) addField(config, 'AuthorizationParty', partyName, details)

    return details
  }, [ucc, config])

  return (
    <DetailCard
      label={`Authorizing Party ${
        party.IsDebtor === 'Y' ? '(Debtor)' : party.IsDebtor === 'N' ? `(Secured Party)` : ``
      }`}
      className="detailsCard"
    >
      {notAvailable && <h4>Not Available</h4>}
      {authorizingPartyDetails.length > 0 && (
        <DetailList details={authorizingPartyDetails} topBorder bottomBorder />
      )}
    </DetailCard>
  )
}

const ActionList = ({ ucc }) => {
  const partyCountString = useMemo(() => {
    const partyCount = ucc.Debtors.length + ucc.SecuredParties.length
    return partyCount > 0 ? ` (${partyCount})` : ``
  }, [ucc])

  // if (ucc.IsLegacy && ucc.IsLegacy === 'Y') {
  //   return (
  //     <React.Fragment>
  //       {ucc.Actions.map((action, index) => {
  //         const isLast = index === ucc.Actions.length - 1
  //         switch (action.Name) {
  //           case 'Add Debtor':
  //           case 'Change Debtor':
  //           case 'Delete Debtor':
  //             return <React.Fragment key={index}>Debtor Update{!isLast && <br />}</React.Fragment>
  //           case 'Add Secured Party':
  //           case 'Change Secured Party':
  //           case 'Delete Secured Party':
  //             return (
  //               <React.Fragment key={index}>Secured Party Update{!isLast && <br />}</React.Fragment>
  //             )
  //           default:
  //             return (
  //               <React.Fragment key={index}>
  //                 {action.Name}
  //                 {!isLast && <br />}
  //               </React.Fragment>
  //             )
  //         }
  //       })}
  //     </React.Fragment>
  //   )
  // } else {
  return (
    <React.Fragment>
      {ucc.Actions.map((action, index) => {
        const isLast = index === ucc.Actions.length - 1
        switch (action.Name) {
          case 'Add Debtor':
          case 'Change Debtor':
          case 'Delete Debtor':
          case 'Add Secured Party':
          case 'Change Secured Party':
          case 'Delete Secured Party':
            return (
              <React.Fragment key={index}>
                {action.Name}
                {partyCountString}
                {!isLast && <br />}
              </React.Fragment>
            )
          default:
            return (
              <React.Fragment key={index}>
                {action.Name}
                {!isLast && <br />}
              </React.Fragment>
            )
        }
      })}
    </React.Fragment>
  )
  // }
}

const DocumentInformation = ({ ucc }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { notaryRecordDetailsSettings: config } = useSelector((state) => state.record, shallowEqual)
  const minDate = '0001-01-01T00:00:00'
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)

  const showPriorLapseDate = useMemo(() => {
    return (
      (ucc.Actions || []).find((action) => action.Name === 'Continuation') &&
      ucc.FilingChangeLapseDate &&
      +ucc.Status === 'Pending'
    )
  }, [ucc])

  const receiptNum = useMemo(() => {
    let receipt = {}
    if (ucc.ReceiptNum !== '') {
      // if (ucc.ReceiptNum === 'Multiple') {
      //   receipt = {
      //     num: (
      //       <div style={{ display: 'flex', flexDirection: 'column' }}>
      //         {(ucc.ReceiptNumbers || []).map((receiptNum, i) => {
      //           return (
      //             <div
      //               key={i}
      //               className="dlnTableLink"
      //               onClick={() => {
      //                 history.push(`/accounting/receipt/${receiptNum}`)
      //               }}
      //             >
      //               {receiptNum || ''}
      //             </div>
      //           )
      //         })}
      //       </div>
      //     ),
      //     amount: ucc.Amount,
      //   }
      // } else {
      receipt = {
        num: ucc.ReceiptNum,
        amount: ucc.Amount,
      }
      // }
    } else if (ucc.LegacyReceiptNumber) {
      receipt = { num: ucc.LegacyReceiptNumber }
    } else {
      receipt = { num: 'Not Available' }
    }

    return receipt
  }, [ucc])

  const docInfoDetails = useMemo(() => {
    const details = []
    addField(config, 'FilingNumber', ucc.FilingNumber, details)
    addField(
      config,
      'ReceivedDate',
      format(new Date(ucc.ReceivedDate || null), 'MM/dd/yyyy hh:mm a'),
      details
    )
    addField(
      config,
      'ProcessedDate',
      format(new Date(ucc.ProcessedDate || null), 'MM/dd/yyyy hh:mm a'),
      details
    )

    if (showPriorLapseDate) {
      addField(
        config,
        'LapseDate',
        ucc && ucc.Filing && ucc.Filing.LapseDate !== minDate
          ? format(new Date(ucc.Filing.LapseDate), 'MM/dd/yyyy hh:mm a')
          : 'No Lapse Date',
        details
      )
      addField(
        config,
        'FilingChangeLapseDate',
        ucc && ucc.FilingChangeLapseDate && ucc.FilingChangeLapseDate !== minDate
          ? format(new Date(ucc.FilingChangeLapseDate), 'MM/dd/yyyy hh:mm a')
          : 'No Lapse Date',
        details
      )
    }

    if ((ucc.Actions || []).length > 0) {
      addField(config, 'Actions', <ActionList ucc={ucc} />, details)
    }

    if (+ucc.Fees > 0)
      addField(
        config,
        'Fees',
        `$${Number(ucc.Fees)
          .toFixed(2)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`,
        details
      )
    if (receiptNum && receiptNum.num)
      addField(
        config,
        'ReceiptNum',
        receiptNum.num !== 'Not Available' &&
          receiptNum.num !== '' &&
          appUserRights.includes('ReceiptView') ? (
          <div
            className="dlnTableLink"
            onClick={() => {
              history.push(`/accounting/receipt/${receiptNum.num}`)
            }}
          >
            {receiptNum.num}
          </div>
        ) : (
          receiptNum.num
        ),
        details
      )
    if (receiptNum && receiptNum.amount) {
      addField(
        config,
        'Amount',
        `$${Number(receiptNum.amount)
          .toFixed(2)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`,
        details
      )
    }
    if (ucc.ImageNum)
      addField(
        config,
        'ImageNum',
        <div
          className={ucc.ImageNum.indexOf(',') === -1 ? 'dlnTableLink' : ''}
          onClick={() => {
            return ucc.ImageNum.indexOf(',') === -1
              ? checkDLNValid(
                  ucc.ImageNum,
                  (id) => {
                    dispatch(openSpecificImage(id, ucc.ImageNum))
                  },
                  (dln) => {
                    dispatch(setErrorToast('DLN ' + dln + ' not found'))
                  }
                )
              : null
          }}
        >
          {ucc.ImageNum}
        </div>,
        details
      )

    return details
  }, [ucc, config, receiptNum])

  return (
    <DetailCard label="File Information" customCss={{ flexGrow: 1 }} className="detailsCard">
      <DetailList details={docInfoDetails} topBorder bottomBorder />
    </DetailCard>
  )
}

const InformationStatementDetail = ({ ucc }) => {
  const { notaryRecordDetailsSettings: config } = useSelector((state) => state.record, shallowEqual)

  const basisText = useMemo(() => {
    if (ucc.ClaimStatement) {
      return <div style={{ whiteSpace: 'pre-line' }}>{ucc.ClaimStatement}</div>
    } else {
      return `Basis for claim is stated on original document images.`
    }
  }, [ucc])

  const details = []
  addField(config, 'ClaimStatement', basisText, details)

  if (ucc.ClaimStatement) {
    return (
      <DetailCard label="Information Statement Detail" className="detailsCard">
        <DetailList
          label={ucc.UCCInfoStatementType ? `The ${ucc.UCCInfoStatementType}` : ``}
          customCss={{ h3: { backgroundColor: 'white !important' } }}
          details={details}
          topBorder
          bottomBorder
        />
      </DetailCard>
    )
  } else return null
}

const LienCounties = ({ ucc }) => {
  if (ucc.LienCounties && ucc.LienCounties.length > 0) {
    return (
      <DetailCard label="County Information" className="detailsCard">
        <div className="county-card-row">
          {ucc.LienCounties.map((county, i) => {
            return <CountyInfo key={`county-info-${i}`} countyInfo={county} index={i} />
          })}
        </div>
      </DetailCard>
    )
  } else return null
}

const DebtorsAndSecuredParties = ({ ucc }) => {
  const debtorHeader = useMemo(() => {
    let str = 'Debtors',
      numChange = 0,
      numNew = 0,
      numDelete = 0
    // if (ucc.IsLegacy && ucc.IsLegacy === 'Y') return str

    ucc.Actions.map((t) => {
      if (t.Name === 'Change Debtor') numChange += 1
      if (t.Name === 'Add Debtor') numNew += 1
      if (t.Name === 'Delete Debtor') numDelete += 1
      return null
    })

    const len = ucc.Debtors.length
    if (numDelete > 0) str = `Debtor${len > 1 ? `s` : ``} Deleted`
    if (numNew > 0) str = `Debtor${len > 1 ? `s` : ``} Added`
    if (numChange > 0) str = `Debtor${len > 2 ? `s` : ``} Changed`

    return str
  }, [ucc])

  const securedPartyHeader = useMemo(() => {
    let str = 'Secured Parties',
      numChange = 0,
      numNew = 0,
      numDelete = 0,
      numAssigned = 0
    // if (ucc.IsLegacy && ucc.IsLegacy === 'Y') return str

    ucc.Actions.map((t) => {
      if (t.Name === 'Add Secured Party') numNew += 1
      if (t.Name === 'Change Secured Party') numChange += 1
      if (t.Name === 'Delete Secured Party') numDelete += 1
      if (t.Name === 'Assignment') numAssigned += 1
      return null
    })

    const len = ucc.SecuredParties.length
    if (numAssigned > 0) str = `Secured Part${len > 1 ? `ies` : `y`} Assigned`
    if (numDelete > 0) str = `Secured Part${len > 1 ? `ies` : `y`} Deleted`
    if (numNew > 0) str = `Secured Part${len > 1 ? `ies` : `y`} Added`
    if (numChange > 0) str = `Secured Part${len > 1 ? `ies` : `y`} Changed`

    return str
  }, [ucc])

  if (ucc.Debtors.length > 0 || ucc.SecuredParties.length > 0) {
    return (
      <DetailCard label={`Debtors / Secured Parties`} className="detailsCard">
        <div className="ucc-card-row">
          {ucc.Debtors.length > 0 && (
            <div className="ucc-card-col">
              <Detail label={debtorHeader}>
                <div className="ucc-card-col">
                  {ucc.Debtors.map((debtor, i) => {
                    return (
                      <React.Fragment key={`debtor-${i}`}>
                        <PartyInfo partyInfo={debtor} index={i} type={'Debtor'} />
                        {i !== ucc.Debtors.length - 1 && <br />}
                      </React.Fragment>
                    )
                  })}
                </div>
              </Detail>
            </div>
          )}
          {ucc.SecuredParties.length > 0 && (
            <div className="ucc-card-col">
              <Detail label={securedPartyHeader}>
                <div className="ucc-card-col">
                  {ucc.SecuredParties.map((sp, i) => {
                    return (
                      <React.Fragment key={`securedParty-${i}`}>
                        <PartyInfo partyInfo={sp} index={i} type={'Secured Party'} />
                        {i !== ucc.SecuredParties.length - 1 && <br />}
                      </React.Fragment>
                    )
                  })}
                </div>
              </Detail>
            </div>
          )}
        </div>
      </DetailCard>
    )
  } else return null
}

const PartyInfo = ({ partyInfo, index, type }) => {
  const { notaryRecordDetailsSettings: config } = useSelector((state) => state.record, shallowEqual)

  const getPartyName = useCallback((party) => {
    if (party.OrganizationName !== '' && party.OrganizationName !== null)
      return party.OrganizationName

    return `${party.LastName ? party.LastName : ``}${
      party.FirstName ? `, ${party.FirstName}` : ``
    }${party.MiddleName ? ` ${party.MiddleName}` : ``}${party.Suffix ? ` ${party.Suffix}` : ``}`
  }, [])

  const from = useMemo(() => {
    return partyInfo.ChangedFromJson ? JSON.parse(partyInfo.ChangedFromJson) : null
  }, [partyInfo])

  const showChangedFrom = useMemo(() => {
    return (
      from && partyInfo.UCCPartyActionTypeId === 3 // changed to
    )
  }, [partyInfo, from, type])

  const details = useMemo(() => {
    const details = []
    if (showChangedFrom) {
      details.push({
        label: '',
        value: (
          <div className="history-changed">
            <div className="from header">Changed From</div>
            <div className="to header">Changed To</div>
          </div>
        ),
      })
      if (
        getPartyName(partyInfo) !== '' ||
        getPartyName(from.Name) !== '' ||
        partyInfo.UIN ||
        from.UIN ||
        partyInfo.TinNumber ||
        from.TinNumber
      ) {
        if (getPartyName(partyInfo) !== '' || getPartyName(from) !== '')
          addField(
            config,
            'PartyName',
            <div className="history-changed">
              <div className="from">{getPartyName(from)}</div>
              <div className="to">{getPartyName(partyInfo)}</div>
            </div>,
            details
          )
        if (notNullOrUndefined(from.IsOrganization) || notNullOrUndefined(partyInfo.IsOrganization))
          addField(
            config,
            'PartyType',
            <div className="history-changed">
              <div className="from">{from.IsOrganization ? 'Organization' : 'Individual'}</div>
              <div className="to">{partyInfo.IsOrganization ? 'Organization' : 'Individual'}</div>
            </div>,
            details
          )
        if (partyInfo.UIN || from.UIN)
          addField(
            config,
            'PartyUIN',
            <div className="history-changed">
              <div className="from">{from.UIN}</div>
              <div className="to">{partyInfo.UIN}</div>
            </div>,
            details
          )
        if (partyInfo.TinNumber || from.TinNumber)
          addField(
            config,
            'TinNumber',
            <div className="history-changed">
              <div className="from">{from.TinNumber}</div>
              <div className="to">{partyInfo.TinNumber}</div>
            </div>,
            details
          )
        addField(
          config,
          'PartyAddress',
          <div className="history-changed">
            <div className="from">{`${from.MultiLineAddress}`}</div>
            <div className="to">{`${partyInfo.MultiLineAddress}`}</div>
          </div>,
          details
        )
        if (type === 'Secured Party')
          addField(
            config,
            'PartyIsAssignee',
            <div className="history-changed">
              <div className="from">{from.IsAssignee ? 'Yes' : 'No'}</div>
              <div className="to">{partyInfo.IsAssignee ? 'Yes' : 'No'}</div>
            </div>,
            details
          )
        if (partyInfo.IsCurrent !== undefined)
          addField(
            config,
            'PartyIsCurrent',
            <div className="history-changed">
              <div className="from">{from.IsCurrent ? 'Yes' : 'No'}</div>
              <div className="to">{partyInfo.IsCurrent ? 'Yes' : 'No'}</div>
            </div>,
            details
          )
      }
    } else {
      if (getPartyName(partyInfo) !== '' || partyInfo.UIN) {
        if (partyInfo !== '') addField(config, 'PartyName', getPartyName(partyInfo), details)
        addField(
          config,
          'PartyType',
          partyInfo.IsOrganization ? 'Organization' : 'Individual',
          details
        )
        if (partyInfo.UIN) addField(config, 'PartyUIN', partyInfo.UIN, details)
        if (partyInfo.TinNumber) addField(config, 'TinNumber', partyInfo.TinNumber, details)
        addField(config, 'PartyAddress', `${partyInfo.MultiLineAddress}`, details)
        if (type === 'Secured Party')
          addField(config, 'PartyIsAssignee', partyInfo.IsAssignee ? 'Yes' : 'No', details)
        if (partyInfo.IsCurrent !== undefined)
          addField(config, 'PartyIsCurrent', partyInfo.IsCurrent ? 'Yes' : 'No', details)
      }
    }
    return details
  }, [partyInfo, showChangedFrom, from, config, type])

  return (
    <DetailCard
      className="detailsCard sub-card"
      label={`${type} ${index + 1} ${/*partyInfo.Action ? `Action: ${partyInfo.Action}` : */ ``}`}
      customCss={{ width: '100%' }}
    >
      <DetailList
        details={details}
        label={''}
        topBorder
        bottomBorder
        className="history-changed-wrapper"
        customCss={
          showChangedFrom
            ? {
                h3: { backgroundColor: 'white !important' },
                'div:nth-child(even)': { padding: '0px' },
              }
            : { h3: { backgroundColor: 'white !important' } }
        }
      />
    </DetailCard>
  )
}

const Collateral = ({ ucc }) => {
  const { notaryRecordDetailsSettings: config } = useSelector((state) => state.record, shallowEqual)

  const collateralHeader = useMemo(() => {
    let header = 'Collateral'
    if (ucc.Actions.some((action) => action.Name === 'Add Collateral')) {
      header = 'Collateral Added'
    } else if (ucc.Actions.some((action) => action.Name === 'Delete Collateral')) {
      header = 'Collateral Deleted'
    } else if (ucc.Actions.some((action) => action.Name === 'Restate Collateral')) {
      header = 'Collateral Restated'
    } else if (ucc.Actions.some((action) => action.Name === 'Collateral Assignment')) {
      header = 'Collateral Assigned'
    }
    return header
  }, [ucc])

  const collateral = useMemo(() => {
    return ucc.UCCCollateral
  }, [ucc])

  const showType = useMemo(() => {
    if (collateral.CollateralIsHeldInTrust) return true
    if (collateral.CollateralIsAdministeredByRepresentative) return true
    if (collateral.IsProceedsCovered) return true
    if (collateral.IsProductsCovered) return true
    return false
  }, [collateral])

  const collateralDetails = useMemo(() => {
    const details = []
    if (!collateralHeader) return null

    addField(
      config,
      'UCCCollateral',
      collateral.CollateralDesc || collateral.AdditionalCollateral ? (
        <div style={{ whiteSpace: 'pre-line' }}>
          {collateral.CollateralDesc}
          {collateral.AdditionalCollateral
            ? `\nAdditional Collateral is specified in the original document images.`
            : null}
        </div>
      ) : (
        `Collateral is specified on the original document image.`
      ),
      details
    )

    if (showType) {
      addField(
        config,
        'UCCCollateralType',
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {collateral.IsProceedsCovered ? (
            <h4 className="detail-h4">PROCEEDS of collateral are also covered</h4>
          ) : null}
          {collateral.IsProductsCovered ? (
            <h4 className="detail-h4">PRODUCTS of collateral are also covered</h4>
          ) : null}
          {collateral.CollateralIsHeldInTrust ? (
            <h4 className="detail-h4">Held in a Trust</h4>
          ) : null}
          {collateral.CollateralIsAdministeredByRepresentative ? (
            <h4 className="detail-h4">Being administered by a Decedent's Personal Rep</h4>
          ) : null}
        </div>,
        details
      )
    }

    return details
  }, [showType, config, collateral])

  if (ucc.UCCCollateral)
    return (
      <DetailCard label={collateralHeader} className="detailsCard">
        <DetailList details={collateralDetails} topBorder bottomBorder />
      </DetailCard>
    )
  else return null
}

const Miscellaneous = ({ ucc }) => {
  const { notaryRecordDetailsSettings: config } = useSelector((state) => state.record, shallowEqual)

  const miscDetails = useMemo(() => {
    const details = []

    if (ucc.FilerReference && ucc.FilerReference !== '')
      addField(config, 'FilerReference', ucc.FilerReference, details)
    if (ucc.LegacyReceiptNumber)
      addField(config, 'LegacyReceiptNumber', ucc.LegacyReceiptNumber, details)
    if (ucc.LegacyRollNumber) addField(config, 'LegacyRollNumber', ucc.LegacyRollNumber, details)
    if (ucc.AddendumSerialNumber)
      addField(config, 'AddendumSerialNumber', ucc.AddendumSerialNumber, details)
    if (ucc.MiscellaneousAddendum)
      addField(config, 'MiscellaneousAddendum', ucc.MiscellaneousAddendum, details)
    if (ucc.MiscInfo)
      addField(
        config,
        'MiscInfo',
        <div style={{ whiteSpace: 'pre-line' }}>{ucc.MiscInfo}</div>,
        details
      )
    if (ucc.CoversTimber || ucc.CoversTimber === false) {
      addField(config, 'CoversTimber', ucc.CoversTimber ? 'Yes' : 'No', details)
    }
    if (ucc.CoversExtracted || ucc.CoversExtracted === false) {
      addField(config, 'CoversExtracted', ucc.CoversExtracted ? 'Yes' : 'No', details)
    }
    if (ucc.IsFixtureFiling || ucc.IsFixtureFiling === false) {
      addField(config, 'IsFixtureFiling', ucc.IsFixtureFiling ? 'Yes' : 'No', details)
    }
    if (ucc.FileInRealEstate || ucc.FileInRealEstate === false) {
      addField(config, 'FileInRealEstate', ucc.FileInRealEstate ? 'Yes' : 'No', details)
    }
    if (ucc.RealEstateOwnerAddress) {
      addField(
        config,
        'RealEstateOwnerAddress',
        <div style={{ whiteSpace: 'pre-line' }}>{ucc.RealEstateOwnerAddress}</div>,
        details
      )
    }
    if (ucc.RealEstateDescription) {
      addField(
        config,
        'RealEstateDescription',
        <div style={{ whiteSpace: 'pre-line' }}>{ucc.RealEstateDescription}</div>,
        details
      )
    }
    return details
  }, [ucc, config])

  if (miscDetails.length > 0) {
    return (
      <DetailCard label={`Miscellaneous`} className="detailsCard">
        <DetailList details={miscDetails} topBorder bottomBorder />
      </DetailCard>
    )
  } else return null
}

const EFSInfo = () => {
  const { ucc } = useSelector((state) => state.record, shallowEqual)
  const { FarmProducts = [] } = ucc
  if (!FarmProducts || FarmProducts.length === 0) return null

  return (
    <DetailCard label="EFS Information" className="detailsCard">
      <div className="efs-card-row">
        {FarmProducts.map((product, i) => {
          return <ProductInfo key={`product-info-${i}`} product={product} index={i} />
        })}
      </div>
    </DetailCard>
  )
}

const CountyInfo = ({ countyInfo, index }) => {
  const { notaryRecordDetailsSettings: config } = useSelector((state) => state.record, shallowEqual)

  const countyDetails = useMemo(() => {
    const list = []
    if (config) {
      addField(config, 'CountyName', countyInfo.County.Name, list)
    }

    return list
  }, [countyInfo, config])

  return (
    <DetailCard
      className="detailsCard sub-card"
      label={`${countyInfo.UCCActionType.Name} County ${index + 1}`}
    >
      <DetailList
        details={countyDetails}
        className="history-changed-wrapper"
        customCss={{ h3: { backgroundColor: 'white !important' } }}
        topBorder
        bottomBorder
      />
    </DetailCard>
  )
}

const ProductInfo = ({ product, index }) => {
  const { notaryRecordDetailsSettings: config } = useSelector((state) => state.record, shallowEqual)

  const from = useMemo(() => {
    return product.ChangedFromJson ? JSON.parse(product.ChangedFromJson) : null
  }, [product])

  const showChangedFrom = useMemo(() => {
    return (
      from && product.UCCActionType.UCCPartyActionTypeId === 3 // changed to
    )
  }, [product, from])

  const productDetails = useMemo(() => {
    const list = []
    if (showChangedFrom) {
      list.push({
        label: '',
        value: (
          <div className="history-changed">
            <div className="from header">Changed From</div>
            <div className="to header">Changed To</div>
          </div>
        ),
      })
      addField(
        config,
        'ProductType',
        <div className="history-changed">
          <div className="from">{(from.EFSFarmProductType || {}).Name}</div>
          <div className="to">{(product.EFSFarmProductType || {}).Name}</div>
        </div>,
        list
      )
      addField(
        config,
        'ProductCounty',
        <div className="history-changed">
          <div className="from">{(from.County || {}).Name}</div>
          <div className="to">{(product.County || {}).Name}</div>
        </div>,
        list
      )
      addField(
        config,
        'ProductYear',
        <div className="history-changed">
          <div className="from">{(from.EFSProductYearType || {}).Name}</div>
          <div className="to">{(product.EFSProductYearType || {}).Name}</div>
        </div>,
        list
      )
      config.ProductEndYear &&
        addField(
          config,
          'ProductEndYear',
          <div className="history-changed">
            <div className="from">{from.EndYear != null ? from.EndYear : 'ALL'}</div>
            <div className="to">{product.EndYear != null ? product.EndYear : 'ALL'}</div>
          </div>,
          list
        )
      addField(
        config,
        'ProductDescription',
        <div className="history-changed">
          <div className="from">{from.AdditionalDescription}</div>
          <div className="to">{product.AdditionalDescription}</div>
        </div>,
        list
      )
      addField(
        config,
        'QuantityDescription',
        <div className="history-changed">
          <div className="from">{from.QuantityDescription}</div>
          <div className="to">{product.QuantityDescription}</div>
        </div>,
        list
      )
    } else {
      addField(config, 'ProductType', (product.EFSFarmProductType || {}).Name, list)
      addField(config, 'ProductCounty', (product.County || {}).Name, list)
      addField(config, 'ProductYear', (product.EFSProductYearType || {}).Name, list)
      config.ProductEndYear &&
        addField(config, 'ProductEndYear', product.EndYear != null ? product.EndYear : 'ALL', list)
      addField(config, 'ProductDescription', product.AdditionalDescription, list)
      addField(config, 'QuantityDescription', product.QuantityDescription, list)
      addField(config, 'Status', product.IsActive ? "Active" : "Inactive", list)
    }
    return list
  }, [product, config, showChangedFrom])

  return (
    <DetailCard
      className="detailsCard sub-card"
      label={`${product.UCCActionType.Name} Product ${index + 1}`}
    >
      <DetailList
        details={productDetails}
        className="history-changed-wrapper"
        customCss={
          showChangedFrom
            ? {
                h3: { backgroundColor: 'white !important' },
                'div:nth-child(even)': { padding: '0px' },
              }
            : { h3: { backgroundColor: 'white !important' } }
        }
        topBorder
        bottomBorder
      />
    </DetailCard>
  )
}

const VoidInfo = ({ ucc }) => {
  const { notaryRecordDetailsSettings: config } = useSelector((state) => state.record, shallowEqual)

  const info = useMemo(() => {
    return ucc.VoidDeleteLog || {}
  }, [ucc])

  const details = []
  addField(
    config,
    'VoidReason',
    <div style={{ whiteSpace: 'pre-line' }}>{info.VoidReason}</div>,
    details
  )

  if (/*ucc.Status === 'Voided' &&*/ info && info.VoidReason) {
    return (
      <DetailCard label="Void Information" className="detailsCard">
        <DetailList
          label={`Voided on ${format(new Date(info.VoidDate), 'MM/dd/yyyy')} by ${info.VoidedBy}`}
          customCss={{ h3: { backgroundColor: 'white !important' } }}
          details={details}
          topBorder
          bottomBorder
        />
      </DetailCard>
    )
  } else return null
}

const OfficerCorrectionInfo = ({ ucc }) => {
  const { notaryRecordDetailsSettings: config } = useSelector((state) => state.record, shallowEqual)
  if (ucc.OfficerCorrectionInfo && ucc.OfficerCorrectionInfo.length > 0) {
    return (
      <DetailCard label="Officer Correction Information" className="detailsCard">
        <div className="ucc-card-col">
          {ucc.OfficerCorrectionInfo.map((oc, i) => {
            const details = []
            addField(config, 'OfficerCorrectionInfoDLN', oc.DLN, details)
            return (
              <DetailCard
                key={i}
                className="detailsCard sub-card"
                label={`Correction ${i + 1}`}
                customCss={{ width: '100%' }}
              >
                <DetailList
                  label={`Corrected on ${format(new Date(oc.Date), 'MM/dd/yyyy hh:mm a')} by ${
                    oc.AppUserFullName
                  }`}
                  customCss={{ h3: { backgroundColor: 'white !important' } }}
                  details={details}
                  topBorder
                  bottomBorder
                />
              </DetailCard>
            )
          })}
        </div>
      </DetailCard>
    )
  } else return null
}

const DishonoredPaymentInfo = ({ ucc }) => {
  const { notaryRecordDetailsSettings: config } = useSelector((state) => state.record, shallowEqual)
  if (ucc.PaymentDelinquentInfo && ucc.PaymentDelinquentInfo.length > 0) {
    return (
      <DetailCard label="Void - Dishonored Payment Information" className="detailsCard">
        <div className="ucc-card-col">
          {ucc.PaymentDelinquentInfo.map((pd, i) => {
            const details = []
            addField(config, 'PaymentDelinquentInfoDLN', pd.DLN, details)
            addField(
              config,
              'PaymentDelinquentInfoReason',
              <div style={{ whiteSpace: 'pre-line' }}>{pd.Reason}</div>,
              details
            )
            return (
              <DetailCard
                key={i}
                className="detailsCard sub-card"
                label={`Dishonored Payment ${i + 1}`}
                customCss={{ width: '100%' }}
              >
                <DetailList
                  label={`Filed on ${format(new Date(pd.Date), 'MM/dd/yyyy hh:mm a')} by ${
                    pd.AppUserName
                  }`}
                  customCss={{ h3: { backgroundColor: 'white  !important' } }}
                  details={details}
                  topBorder
                  bottomBorder
                />
              </DetailCard>
            )
          })}
        </div>
      </DetailCard>
    )
  } else return null
}

const DownloadDocuments = () => {
  const { ucc } = useSelector((state) => state.record, shallowEqual)
  const [loading, setLoading] = useState(false)
  const value = ucc.CorrespondenceLink
  const reportsInfo = useRequestData(
    (cb, errCb) => {
      if (value && !value.includes('/report//')) {
        setLoading(true)
        api.GET(
          value,
          (val) => {
            setLoading(false)
            cb(val)
          },
          (err) => {
            setLoading(false)
            errCb(err)
          }
        )
      } else {
        setLoading(false)
        // console.warn(value, 'Not a valid Report URL.')
        cb([])
      }
    },
    [value],
    []
  )
  return (
    <DetailCard label="Downloads" className="detailsCard">
      {loading ? (
        <div style={{ marginTop: '8px', marginLeft: '10px', marginBottom: '8px' }}>
          <Spinner themeColor="dark-neutral" size="small" />
        </div>
      ) : (
        <div className="record-detail-reports-list">
          {(reportsInfo || []).map((report, i) => {
            return <Report key={i} report={report} delay={50 * i} />
          })}
          {reportsInfo.length === 0 ? <h4>No Reports</h4> : null}
        </div>
      )}
    </DetailCard>
  )
}

const Corrections = () => {
  const { ucc } = useSelector((state) => state.record, shallowEqual)
  const { Corrections } = ucc

  const correctionHistory = useMemo(() => {
    // console.log(Corrections)
    const amendmentList = []
    const historyList = []

    Corrections.sort((a, b) => {
      const eh = new Date(a.FilingDate).getMilliseconds()
      const be = new Date(b.FilingDate).getMilliseconds()
      return eh > be ? -1 : 1
    }).forEach((item) => {
      amendmentList.push({
        AMENDMENT_TYPE: 'Staff Correction',
        AMENDMENT_DATE: format(new Date(item.FilingDate), 'MM/dd/yyyy'),
        AMENDMENT_ID: item.StaffFilingCorrectionId,
        CorrectedBy: item.FiledBy.FullName,
      })
      item.StaffFilingCorrectionHistories.forEach((history) => {
        historyList.push({
          AMENDMENT_ID: item.StaffFilingCorrectionId,
          APP_VIEW_YN: 'N',
          CHANGED_FROM: history.ChangedFrom,
          CHANGED_TO: history.ChangedTo,
          DISPLAY_NAME: history.StaffFilingCorrectionHistoryType.DisplayName,
          FIELD_NAME: history.StaffFilingCorrectionHistoryType.Name,
          HISTORY_TYPE_ID: history.StaffFilingCorrectionHistoryId,
          WEB_VIEW_YN: 'Y',
        })
      })
    })

    return {
      amendments: amendmentList,
      history: historyList,
      template: [{ label: 'Corrected By', id: 'CorrectedBy', type: '' }],
    }
  }, [Corrections])

  if (!Corrections || Corrections.length === 0) return null

  return <RecordHistory history={correctionHistory} />
}

// const dummyUcc = {
//   Submitter: {
//     SubmitterName: 'Astrid',
//     ContactName: 'Hiccup',
//     ContactPhone: '801-552-0076',
//     ContactEmail: 'hiccup.berk@httyd.not',
//     Address1: 'PO Box 2thl3ss',
//     Address2: '',
//     Address3: '',
//     City: 'Tavern',
//     State: 'Berk',
//     ZipCode: '54321',
//     Country: 'USA',
//   },
//   AmendmentTypeID: 5,
//   AmendmentType: 'Lien Statement of Claim',
//   Party: {
//     IsDebtor: 'Y',
//     OrganizationName: 'Dragon Riders',
//     FirstName: 'Stoic',
//     MiddleName: 'The',
//     LastName: 'Vast',
//     Suffix: 'Chief',
//     PartyID: 0,
//     UIN: null,
//     ActionID: 0,
//     Action: null,
//     Address1: null,
//     Address2: null,
//     Address3: null,
//     City: null,
//     State: null,
//     Zip: null,
//     Country: null,
//   },
//   AdditionalInfo: {
//     Line1b: 'Testing line1b',
//     // "IsAdditionalCollateral": true,
//   },
//   RecordStatusID: 551,
//   RecordStatus: 'Pending',
//   Filing: {
//     UCCFilingID: 0,
//     LapseDate: '0001-01-01T00:00:00',
//     LapsedYn: false,
//     StatusID: 0,
//     Status: 'Pending',
//     RecordTypeID: 2174,
//     RecordType: 'Something type',
//     LienTypeID: 23,
//     UCCOtherTypeID: 19,
//     UCCOtherType: 'Test UccOtherType',
//     UCCAltDesignationTypeID: 20,
//     UCCAltDesignationType: 'Test UCCAltDesignationType',
//     LienType: 'Test LienType',
//   },
//   UCCAmendmentID: 3200947,
//   UCCNum: 343234,
//   FilingDate: '2022-01-04T09:59:27.737',
//   FilingChangeLapseDate: '0001-01-01T00:00:00',
//   AmendmentActionTypes: [
//     {
//       UCCAmendmentActionTypeID: 5,
//       UCCAmendmentActionTypeName: 'testing type',
//     },
//     {
//       UCCAmendmentActionTypeID: 9,
//       UCCAmendmentActionTypeName: 'Another type',
//     },
//     {
//       UCCAmendmentActionTypeID: 18,
//       UCCAmendmentActionTypeName: 'Collateral type',
//     },
//   ],
//   // IsLegacy: 'N', // false,
//   FilingFee: 35.37,
//   ReceiptNumber: '',
//   ReceiptsTotal: 35,
//   UCCLegacyPaymentInfo: null,
//   DLN: 'B0296-7306',
//   Correction: {
//     CorrectionTypeID: 2060,
//     CorrectionType: 'Record is inaccurate',
//     CorrectionInfo: 'gold\nsilver\nbronze and other things',
//   },
//   OfficerCorrectionInfo: [
//     {
//       Date: '2022-01-04T09:59:27.737',
//       AppUserFullName: 'Doc Brown',
//       DLN: 'B7890-0987',
//     },
//   ],
//   PaymentDelinquentInfo: [
//     {
//       Date: '2022-01-04T09:59:27.737',
//       AppUserName: 'Doc Brown',
//       DLN: 'B7890-0987',
//       Reason: 'multi\nline\nreason',
//     },
//   ],
//   Debtors: [
//     {
//       UCCDebtorID: 1984648,
//       IsDebtor: false,
//       OrganizationName: null,
//       FirstName: 'UPLOAD',
//       MiddleName: 'B',
//       LastName: 'TEST',
//       Suffix: null,
//       PartyID: 1984648,
//       UIN: null,
//       ActionID: 2033,
//       Action: 'ADD',
//       Address1: '123 Blacksmith Lane',
//       Address2: '',
//       Address3: '',
//       City: 'City',
//       State: 'Berk',
//       Zip: '54321',
//       Country: 'USA',
//       ChangedFrom: {
//         UCCDebtorID: 1984648,
//         IsDebtor: false,
//         OrganizationName: null,
//         FirstName: 'UPLOAD',
//         MiddleName: 'B',
//         LastName: 'TEST',
//         Suffix: null,
//         PartyID: 1984648,
//         UIN: null,
//         ActionID: 2030,
//         Action: 'ADD',
//         Address1: '123 Blacksmith Lane',
//         Address2: '',
//         Address3: '',
//         City: 'City',
//         State: 'Berk',
//         Zip: '54321',
//         Country: 'USA',
//       },
//     },
//     {
//       UCCDebtorID: 1984648,
//       IsDebtor: false,
//       OrganizationName: null,
//       FirstName: 'UPLOAD',
//       MiddleName: 'B',
//       LastName: 'TEST',
//       Suffix: null,
//       PartyID: 1984648,
//       UIN: null,
//       ActionID: 2035,
//       Action: 'ADD',
//       Address1: '123 Blacksmith Lane',
//       Address2: '',
//       Address3: '',
//       City: 'City',
//       State: 'Berk',
//       Zip: '54321',
//       Country: 'USA',
//     },
//   ],
//   SecuredParties: [
//     {
//       IsAssignee: true,
//       SecurePartyID: 1753825,
//       IsDebtor: false,
//       OrganizationName: '',
//       FirstName: 'John',
//       MiddleName: '',
//       LastName: 'Doe',
//       Suffix: 'Master Chief',
//       PartyID: 1753825,
//       UIN: null,
//       ActionID: 2030,
//       Action: 'ADD',
//       Address1: '117 Xbox ST',
//       Address2: '',
//       Address3: '',
//       City: 'Cartographer',
//       State: 'Halo',
//       Zip: '80117',
//       Country: 'USA',
//     },
//   ],
//   Collateral: {
//     UCCCollateralID: 19,
//     UCCAmendmentID: 19,
//     ActionID: 0,
//     Action: '0',
//     CollateralDesc: 'Test\nCollateral Description',
//     IsProceedsCovered: true,
//     IsProductsCovered: true,
//     IsPaySecuredPartyOnly: false,
//     IsPayDebtorOnly: false,
//     IsAdditionalCollateral: true,
//   },
//   FilerReference: 'Test Filer Reference',
//   MiscInfo: 'Test\nMulti\nLine',
//   // Addendum: {
//   //   SerialNumber: '2354',
//   //   MiscInfo: 'Test Addendum misc info',
//   // },
//   LegacyInfo: {
//     ReceiptNumber: '989',
//     RollNumber: '0002',
//   },
//   VoidDeleteInfo: {
//     VoidDeleteDate: '2022-01-04T09:59:27.737',
//     PrimaryAppUserName: 'Luke Skywalker',
//     Reason: 'Multi\nLine\nTest',
//   },
// }
